<script setup lang="ts">
  import { storeToRefs } from "pinia";
  import InputMessage, { type MentionUser } from "~/components/Atoms/InputMessage.vue";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useTalkRoomViewStore } from "~/stores/TalkRoomViewStore";
  import { useUserStore } from "~/stores/UserStore";
  import { TALK_INPUT_TEXT_MAX, TALK_MENTION_PLACEHOLDER } from "~/consts/TalkConfig";
  import type { Talk } from "~/entities/Talk";

  interface Props {
    canSend?: boolean;
    isSending?: boolean;
    talkRoomIdToSave?: string; // 保存先のトークルームID
    talkToEdit?: Talk; // 編集するトーク
    allowEmptySend: boolean; // 空のメッセージを送信できるか
    placeholder: string; // プレースホルダ
    fixedPosition: boolean; // 固定位置で表示するか
    withSendButtonMargin?: boolean; // 送信ボタンのマージンを表示するか
    sendFunc: (message: string, mentionUserIds: string[]) => Promise<void>;
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:canSend", canSend: boolean): void;
    (event: "update:isSending", isSending: boolean): void;
    (event: "sent"): void;
    (event: "failed", error: any): void;
  }
  const emit = defineEmits<Emits>();

  const talkRoomStore = useTalkRoomStore();
  const talkRoomViewStore = useTalkRoomViewStore();
  const { talkRoomMessageInputs } = storeToRefs(talkRoomViewStore);
  const userStore = useUserStore();

  const textInput = ref<null | typeof InputMessage>(null);

  // 入力データ
  const inputMessage = ref<string>(""); // テキスト
  const mentionUserIdList = ref<string[]>([]); // メンションユーザーIDリスト

  // 送信中フラグ
  const nowSending = ref<boolean>(false);

  // 送信処理実行
  const triggerSend = async () => {
    // https://fincs.backlog.com/view/FINCS-3255
    // iOS の場合はこのタイミングでエディタから未確定文字が返ってこない場合があるので、少し待ってから送信する
    if (isIOS()) {
      setTimeout(doSend, 50);
    } else {
      await doSend();
    }
  };

  const doSend = async () => {
    if (!props.allowEmptySend) {
      // 空データは送信しない
      if (!inputMessage.value || inputMessage.value.trim().length === 0) {
        return;
      }
    }
    // 投稿禁止チェック
    if (userStore.isPostProhibited()) {
      return;
    }
    // 二重送信防止
    if (nowSending.value) {
      return;
    }

    // 送信
    let e: any = null;
    nowSending.value = true;
    emit("update:isSending", true);
    try {
      // 送信処理
      await props.sendFunc(inputMessage.value, mentionUserIdList.value);

      // 送信成功後、入力をクリアしてフォーカスを外す
      textInput.value!.setText("");
      textInput.value!.blur();
    } catch (error: any) {
      e = error;
    } finally {
      nowSending.value = false;
      emit("update:isSending", false);

      // isSending を解除してから結果を通知する
      // でないと呼び元の状態がおかしくなる場合あり
      // https://fincs.backlog.com/view/FINCS-3718
      if (e === null) {
        emit("sent");
      } else {
        emit("failed", e);
      }
    }
  };

  const getMentionUsers = async (searchTerm: string) => {
    const users = await talkRoomStore.getMentionUsers(searchTerm);
    return users.map(
      u =>
        ({
          id: u.userId,
          name: u.userName,
          image: u.userImage,
          isOwner: u.isOwner,
        }) as MentionUser,
    );
  };

  const getMentionUserName = (userId: string) => {
    return new Promise<string | null>(resolve => {
      if (userId in talkRoomStore.talkRoomUsers) {
        resolve(talkRoomStore.talkRoomUsers[userId].userName);
      } else {
        resolve(null);
      }
    });
  };

  const onInputChange = (message: string, mentionUserIds: string[], contents: any) => {
    inputMessage.value = message;
    mentionUserIdList.value = mentionUserIds;
    emit("update:canSend", props.allowEmptySend || message.trim().length > 0);

    // 保存先のトークルームIDが指定されている場合は、入力中のメッセージを保存する
    if (props.talkRoomIdToSave) {
      const talkRoomId = props.talkRoomIdToSave;
      const talkRoomMessageInput = talkRoomMessageInputs.value.find(item => item.talkRoomId === talkRoomId);
      if (talkRoomMessageInput) {
        talkRoomMessageInput.message = contents;
      } else {
        talkRoomMessageInputs.value.push({
          talkRoomId: talkRoomId,
          message: contents,
        });
      }
    }
  };

  watch(textInput, () => {
    if (props.talkRoomIdToSave) {
      // 保存先のトークルームIDが指定されている場合は読み出し
      const talkRoomMessageInput = talkRoomMessageInputs.value.find(item => item.talkRoomId === props.talkRoomIdToSave);
      if (talkRoomMessageInput) {
        // 保存データあるので適用
        if (typeof talkRoomMessageInput.message === "string") {
          // 旧式の string 保存の場合は setText
          textInput.value!.setText(talkRoomMessageInput.message);
        } else {
          // 新式保存の場合は setContents
          textInput.value!.setContents(talkRoomMessageInput.message);
        }
      } else {
        // 保存データないので空にする
        textInput.value!.setText("");
      }
    } else if (props.talkToEdit) {
      // 編集するトークが指定されている場合は読み出し
      const talk = props.talkToEdit;
      textInput.value!.setTextAndMentions(
        (talk.messageType === "text" ? talk.contents : talk.note) ?? "",
        talk.mentionUserIds ?? [],
      );
    } else {
      // 空文字列で初期化
      textInput.value!.setText("");
    }
  });

  const focus = () => {
    textInput.value!.focus();
  };

  defineExpose({
    triggerSend,
    focus,
  });
</script>

<template>
  <div>
    <AtomsInputMessage
      ref="textInput"
      class="talk-input-message"
      :class="withSendButtonMargin ? 'talk-input-message-with-send-button' : null"
      :placeholder="placeholder"
      :max-text-length="TALK_INPUT_TEXT_MAX"
      :mention-placeholder="TALK_MENTION_PLACEHOLDER"
      :get-mention-users="getMentionUsers"
      :get-mention-user-name="getMentionUserName"
      :fixed-position="fixedPosition"
      data-testid="talk-input-message"
      @change="onInputChange"
      @done="triggerSend"
    />
  </div>
</template>

<style lang="scss" scoped>
  .talk-input-message {
    word-break: break-word;
  }
  .talk-input-message-with-send-button {
    padding-right: 40px;
  }
</style>
